import React, { useMemo } from 'react';
import {
  Flex,
  GoodsCarouselSkeleton,
  List,
  useScreenSize,
} from '@banhala/ui-kit';
import { useAtomicProxyComponent } from './queries/useAtomicProxyComponent';
import { useLikeMap, useOutfitBookmarkMap } from '../../../../hooks';
import { AtomicComponent } from '../../utils/createAtomicComponent';
import type { IScreensLogging } from '../../../../api/types';
import { ProxySkeletonType } from '../../../../api/screensService/getScreens/enums';
import type { LayoutChangeEvent } from 'react-native';

type ProxyBody = {
  item: {
    url: string;
    skeleton_type?: ProxySkeletonType;
  };
};

const ProxyContent: React.FC<{
  screenName?: string;
  screen?: string;
  body: ProxyBody;
  logging?: IScreensLogging;
  logDisabled?: boolean;
  isVisible?: boolean;
  isPendingScroll?: boolean;
  isAwaitingDataChange?: boolean;
  onReady?: (event: LayoutChangeEvent) => void;
}> = props => {
  const {
    data: proxyComponent,
    isSuccess,
    isLoading,
  } = useAtomicProxyComponent(props.body.item.url, props.logging);
  const { likeMap, setLikeMap } = useLikeMap();
  const { bookmarkMap, setBookmarkMap } = useOutfitBookmarkMap({
    initialMap: {},
  });

  const skeletonType =
    props.body.item.skeleton_type ?? ProxySkeletonType.GoodsCarousel;

  if (isLoading) {
    switch (skeletonType) {
      case ProxySkeletonType.Hidden:
        return <Flex height={1} />;
      case ProxySkeletonType.GoodsCarousel:
        return <ProxyCarouselSkeleton />;
      default:
        return <ProxyGridSkeleton />;
    }
  }
  if (!isSuccess) return null;

  return (
    <Flex
      onLayout={event => {
        props.onReady?.(event);
      }}
    >
      {proxyComponent?.components.map((component, index) => {
        return (
          <Flex key={`atomic-proxy-item-${index}`}>
            <AtomicComponent
              component={component}
              index={index}
              likeMap={likeMap}
              setLikeMap={setLikeMap}
              bookmarkMap={bookmarkMap}
              setBookmarkMap={setBookmarkMap}
              screen={props.screen}
              screenLogging={props.logging}
              logDisabled={props.logDisabled}
              isVisible={props.isVisible}
              isPendingScroll={props.isPendingScroll}
              isAwaitingDataChange={props.isAwaitingDataChange}
              screenName={props.screenName}
            />
          </Flex>
        );
      })}
    </Flex>
  );
};

const ProxyCarouselSkeleton = () => {
  return (
    <Flex>
      <Flex.Row
        padding={{ horizontal: 16, vertical: 16 }}
        alignItems={'center'}
        justifyContent={'space-between'}
      >
        <Flex
          backgroundColor={'bg_tertiary'}
          width={98}
          height={24}
          borderRadius={4}
        />
        <Flex
          backgroundColor={'bg_tertiary'}
          width={64}
          height={18}
          borderRadius={4}
        />
      </Flex.Row>
      <Flex height={200}>
        <GoodsCarouselSkeleton count={4} />
      </Flex>
    </Flex>
  );
};

const HORIZONTAL_SPACE = 2;

const ProxyGridSkeleton = () => {
  const { width } = useScreenSize();

  const itemSize = useMemo(() => {
    const numColumns = 3;

    const itemWidth =
      (width - HORIZONTAL_SPACE * numColumns) * (1 / numColumns);

    return {
      width: itemWidth,
      height: itemWidth,
    };
  }, [width]);

  const cardWidth = itemSize.width;

  return (
    <Flex
      onLayout={event => {
        console.log(event.nativeEvent);
      }}
    >
      <Flex.Row
        padding={{ horizontal: 16, vertical: 16 }}
        alignItems={'center'}
        justifyContent={'space-between'}
      >
        <Flex
          backgroundColor={'bg_tertiary'}
          width={98}
          height={24}
          borderRadius={4}
        />
        <Flex
          backgroundColor={'bg_tertiary'}
          width={64}
          height={18}
          borderRadius={4}
        />
      </Flex.Row>
      <List
        numColumns={3}
        data={[1, 2, 3, 4, 5, 6, 7, 8, 9]}
        columnWrapperStyle={{ gap: 2 }}
        keyExtractor={item => item.toString()}
        renderItem={() => {
          return (
            <Flex width={itemSize.width} height={236}>
              <Flex position={'relative'}>
                <Flex.Center
                  width={cardWidth}
                  height={cardWidth * 1.2}
                  backgroundColor={'bg_tertiary'}
                ></Flex.Center>
                <Flex margin={{ top: 4, bottom: 20 }} gap={2}>
                  <Flex
                    width={cardWidth * 0.8}
                    height={20}
                    backgroundColor={'bg_tertiary'}
                  />
                  <Flex
                    width={cardWidth * 0.9}
                    height={16}
                    backgroundColor={'bg_tertiary'}
                  />
                  <Flex
                    width={cardWidth * 0.4}
                    height={14}
                    margin={{ top: 4 }}
                    backgroundColor={'bg_tertiary'}
                  />
                </Flex>
              </Flex>
              {/*<GoodsCardSkeleton ratio={1.2} width={itemSize.width} />*/}
            </Flex>
          );
        }}
      />
      <Flex padding={{ horizontal: 12, vertical: 12 }}>
        <Flex
          height={56}
          backgroundColor={'bg_tertiary'}
          borderRadius={'medium'}
          alignItems={'center'}
          justifyContent={'center'}
        ></Flex>
      </Flex>
    </Flex>
  );
};

export const Proxy: React.FC<{
  screenName?: string;
  screen?: string;
  body: ProxyBody;
  logging?: IScreensLogging;
  logDisabled?: boolean;
  isVisible?: boolean;
  isPendingScroll?: boolean;
  isAwaitingDataChange?: boolean;
  onReady?: (event: LayoutChangeEvent) => void;
}> = props => {
  return <ProxyContent {...props} />;
};
